import React, { useEffect, useState } from "react"
import { Alert, Checkbox, Input, Modal, Select } from "antd"

import { serializeDatamapCsv } from "features/DataImport/components/DatamapEditor/utils/datamapCsv"
import { NewDatamapRow } from "features/DataImport/components/DatamapEditor/utils/datamapEdits"
import {
  buildWizardRows,
  suggestType,
  WizardInput,
  wizardCodes,
  wizardProblems,
} from "features/DataImport/components/DatamapEditor/utils/wizardRows"
import { columnSummary } from "features/DataImport/components/DatamapEditor/DatamapFindings.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { DATAMAP_TYPES, DatamapDoc, DatamapType, UnmappedColumnFinding } from "types/datamap.types"

interface AddVariableWizardProps {
  open: boolean
  /** Every data column no row reads yet. */
  columns: UnmappedColumnFinding[]
  initialColumn: string | null
  doc: DatamapDoc
  onAdd: (rows: NewDatamapRow[]) => void
  onCancel: () => void
}

const STEP_TITLES = ["Columns", "Labels and type", "Codes", "Check and add"]

/** Adds rows for data columns the questionnaire doesn't have, such as MaxDiff fields or a scripter's late question. */
const AddVariableWizard: React.FC<AddVariableWizardProps> = ({ open, columns, initialColumn, doc, onAdd, onCancel }) => {
  const [step, setStep] = useState(0)
  const [selected, setSelected] = useState<string[]>([])
  const [labels, setLabels] = useState<Record<string, string>>({})
  const [type, setType] = useState<DatamapType>("TEXT")
  const [codeLabels, setCodeLabels] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open || initialColumn === null) return
    const finding = columns.find((column) => column.column === initialColumn)
    setStep(0)
    setSelected([initialColumn])
    setLabels({ [initialColumn]: initialColumn })
    setType(finding ? suggestType(finding) : "TEXT")
    setCodeLabels({})
    // Reset only when the wizard opens for a column.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialColumn])

  const chosen = columns.filter((column) => selected.includes(column.column))
  const codes = wizardCodes(chosen)
  // Only the codes of the chosen columns go into the rows. The state keeps the others, so ticking a column again brings its labels back.
  const currentCodeLabels = Object.fromEntries(codes.filter((c) => codeLabels[c] !== undefined).map((c) => [c, codeLabels[c]]))
  const input: WizardInput = { columns: selected, labels, type, codeLabels: currentCodeLabels }
  const problems = wizardProblems(input, chosen, doc)
  const steps = type === "CAT" ? [0, 1, 2, 3] : [0, 1, 3]
  const position = steps.indexOf(step)

  /** Where the column is in the data file, so the new rows come out in that order whatever order they were ticked in. */
  const dataOrder = (column: string): number => columns.findIndex((c) => c.column === column)

  const toggle = (column: string, on: boolean): void => {
    setSelected(
      on ? [...selected, column].sort((a, b) => dataOrder(a) - dataOrder(b)) : selected.filter((c) => c !== column)
    )
    if (on && labels[column] === undefined) setLabels({ ...labels, [column]: column })
  }

  const preview = (): string =>
    serializeDatamapCsv({
      hasFieldMetadata: false,
      rows: buildWizardRows(input).map((row, i) => ({ ...row, id: `new${i}` })),
    })
      .split("\n")
      .slice(1)
      .join("\n")

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={720}
      centered
      destroyOnHidden
      title={`Add variable: ${STEP_TITLES[step]}`}
    >
      <p className="m-0 mb-3 text-xs text-gray-500">
        Step {position + 1} of {steps.length}
      </p>

      {step === 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex max-h-80 flex-col gap-1 overflow-y-auto">
            {columns.map((column) => (
              <Checkbox key={column.column} checked={selected.includes(column.column)} onChange={(e) => toggle(column.column, e.target.checked)}>
                <span className="text-sm font-medium text-gray-700">{column.column}</span>{" "}
                <span className="text-xs text-gray-500">{columnSummary(column)}</span>
              </Checkbox>
            ))}
          </div>
          <p className="m-0 text-xs text-gray-500">Choose several columns that share their codes, such as the best and worst picks of each MaxDiff set.</p>
        </div>
      )}

      {step === 1 && (
        <div className="flex flex-col gap-3">
          <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
            {selected.map((column) => (
              <label key={column} className="flex items-center gap-2">
                <span className="w-40 shrink-0 truncate text-sm font-medium text-gray-700" title={column}>
                  {column}
                </span>
                <Input
                  aria-label={`Label for ${column}`}
                  value={labels[column] ?? column}
                  onChange={(e) => setLabels({ ...labels, [column]: e.target.value })}
                />
              </label>
            ))}
          </div>
          <p className="m-0 text-xs text-gray-500">Use "//" to put a row under a group, for example MaxDiff//Set 1//Best.</p>
          <div className="flex items-center gap-2">
            <span className="w-40 shrink-0 text-sm font-medium text-gray-700">Type</span>
            <Select
              aria-label="Type"
              className="w-40"
              value={type}
              options={DATAMAP_TYPES.map((t) => ({ value: t, label: t }))}
              onChange={(value: DatamapType) => setType(value)}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
          {codes.map((code) => (
            <label key={code} className="flex items-center gap-2">
              <span className="w-16 shrink-0 text-right text-sm font-medium text-gray-700">{code}</span>
              <Input
                aria-label={`Label for code ${code}`}
                value={codeLabels[code] ?? ""}
                onChange={(e) => setCodeLabels({ ...codeLabels, [code]: e.target.value })}
              />
            </label>
          ))}
        </div>
      )}

      {step === 3 &&
        (problems.length > 0 ? (
          <Alert
            type="error"
            showIcon
            message="Not ready yet"
            description={
              <ul className="m-0 pl-4">
                {problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            }
          />
        ) : (
          <pre className="m-0 max-h-80 overflow-auto rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">{preview()}</pre>
        ))}

      <div className="flex justify-end gap-2 pt-4">
        <DataQButton type="secondary" onClick={onCancel}>
          Cancel
        </DataQButton>
        {position > 0 && (
          <DataQButton type="secondary" onClick={() => setStep(steps[position - 1])}>
            Back
          </DataQButton>
        )}
        {step !== 3 ? (
          <DataQButton type="primary" isDisabled={selected.length === 0} onClick={() => setStep(steps[position + 1])}>
            Next
          </DataQButton>
        ) : (
          <DataQButton type="primary" isDisabled={problems.length > 0} onClick={() => onAdd(buildWizardRows(input))}>
            Add {selected.length === 1 ? "row" : `${selected.length} rows`}
          </DataQButton>
        )}
      </div>
    </Modal>
  )
}

export default AddVariableWizard
